import { useScroll, useSpring } from 'react-spring'
import { useEffect, useRef, useState } from 'react'
import useBreakpoint from 'hooks/useBreakPoint'

type UseHeaderScrollAnimateProps = {
  animation: { hide: string; show: string }
  hideNavOnScroll?: boolean
  hideNavForSupportUsers?: boolean
}

// Couple of spring types wasn't seem to do the job, so added this for now
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useHeaderScrollAnimate = ({
  animation,
  hideNavOnScroll = true,
  hideNavForSupportUsers,
}: UseHeaderScrollAnimateProps) => {
  const [hideNav, setHideNav] = useState<boolean>(false)
  const headerPositionStyle = useSpring({
    transform: hideNav ? animation.hide : animation.show,
  })
  const { isMobileScreen } = useBreakpoint()
  const previousScrollY = useRef(0)

  useEffect(() => {
    if (isMobileScreen && hideNavOnScroll) {
      // On mobile view start the hide/show animation on scroll
      headerPositionStyle.transform.resume()
    } else if (!isMobileScreen && hideNav) {
      // Resets the nav hidden animation after switching to desktop from mobile, to show the nav on desktop and then disable the hide/show nav on scroll.
      // Added this set timeout because for some reason after switching from mobile to desktop, it doesn't seem to set the animation if it done immediately
      setTimeout(() => {
        setHideNav(false)
        setTimeout(() => {
          headerPositionStyle.transform.pause()
        }, 700)
      }, 700)
    } else if (hideNavForSupportUsers && hideNavOnScroll) {
      // For support users, hide the nav bar on desktop screens as well.
      headerPositionStyle.transform.resume()
    } else {
      // Immediately pauses the animation on desktop when switching from mobile to desktop and when the nav is already visible
      headerPositionStyle.transform.pause()
    }
  }, [isMobileScreen, hideNavOnScroll])

  useScroll({
    onChange: ({ value: { scrollY } }) => {
      if (previousScrollY.current - scrollY > 0) {
        // When scrolling to the top
        setHideNav(false)
      } else if (
        previousScrollY.current - scrollY < 0 &&
        // Reason for this condition is because we don't want to hide the nav till the scroll position exceeds the nav height
        // Or is closer to the nav height because the empty space on the first scroll looks ugly
        scrollY > 100
      ) {
        // When scrolling to the bottom
        setHideNav(true)
      }

      previousScrollY.current = scrollY
    },
  })

  return headerPositionStyle
}

export default useHeaderScrollAnimate
